import React, { useCallback, useEffect, useMemo, useState } from "react";
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import PlacesNewAutocomplete from "./PlacesNewAutocomplete";
import { openLatLng } from "../utils/locationCrypto";
import {
  fetchNearbyFuelStations,
  NEARBY_FUEL_STATION_TARGET,
  PRIMARY_FUEL_STATION_LIMIT,
} from "../utils/nearbyFuelStations";

/**
 * Monthly per-litre pump prices for the fleet.
 *
 * Premium / Enterprise: school can pin up to 5 primary stations (real pump
 * Ksh/L). Zana fills the rest from Places Nearby around the school pin (~10
 * unique places total), priced at EPRA ceilings — no overlap with primaries.
 * Admins edit any row; refresh keeps hand-tuned / primary prices.
 *
 * Basic: the school is the price librarian — type/search stations and enter
 * Ksh/L. Target is at least ~10 stations so mobile cost estimates have a shelf.
 *
 * Premium/Enterprise orgs (including pilots on those tiers) get diesel + petrol
 * ceilings filled from EPRA; a station list always wins over those, because a
 * real pump price beats a national ceiling.
 *
 * Places is used to FIND stations, never to price them — it has no live pump
 * Ksh/L. Nearby-seeded rows start at the EPRA ceiling and are meant to be
 * edited; a ceiling is not a price.
 */

export type StationFuelType = "diesel" | "petrol";

export interface FuelPriceStation {
  placeId: string;
  name: string;
  pricePerL: number;
  fuelType: StationFuelType;
  /** School-picked favourite — max 5 unique placeIds. Nearby seed never clears these. */
  primary?: boolean;
}

export interface FuelPricesDoc {
  orgCode: string;
  month: string;
  source: "admin" | "zana" | "epra" | "carried_forward" | "regional_default";
  stations: FuelPriceStation[];
  regionalDefaultPerL?: number | null;
  regionalDefaultPerLByType?: { diesel?: number | null; petrol?: number | null } | null;
  caps?: { diesel?: number | null; petrol?: number | null } | null;
  /** Which EPRA town the ceilings were read from, when source === "epra". */
  epraTown?: string | null;
  carriedFrom?: string;
  note?: string;
  /** Stamped whenever PEAR (console apply / monthly sweep / Refresh button)
      refreshed the EPRA ceilings under this fleet's list. */
  lastEpraCaps?: { diesel?: number | null; petrol?: number | null } | null;
  lastEpraRefreshAt?: Timestamp | null;
}

export function currentMonthKey(d?: Date): string {
  const dt = d ?? new Date();
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

interface Props {
  fleetOrgCode: string;
  onBack?: () => void;
  /** Premium/Enterprise (pilots included) get EPRA auto-fill — gates the EPRA
   *  banner. Prefer this over inventing a second plan check; `isZanaTier` is
   *  now also true for Premium/Enterprise pilots (2026-08-21), so the two
   *  flags align for those orgs. Kept separate for call sites that only care
   *  about fuel-shelf EPRA, not Zana chat. */
  epraManaged?: boolean;
  /** Premium orgs are told Zana keeps this current, rather than nagged to do it.
   *  Gates the Zana station-seeding card only. */
  isZanaTier?: boolean;
  /** When true (Analytics → Fuel shelf), skip the page title / EPRA tip — parent already shows them. */
  embedded?: boolean;
}

const CARD: React.CSSProperties = {
  background: "#fff",
  borderRadius: 14,
  border: "1px solid #e2e8f0",
  padding: 24,
};

export default function FuelPricesPage({
  fleetOrgCode,
  onBack,
  epraManaged,
  isZanaTier,
  embedded,
}: Props) {
  const month = useMemo(() => currentMonthKey(), []);
  const [stations, setStations] = useState<FuelPriceStation[]>([]);
  const [source, setSource] = useState<FuelPricesDoc["source"]>("admin");
  const [epraTown, setEpraTown] = useState<string | null>(null);
  const [carriedFrom, setCarriedFrom] = useState<string | undefined>();
  /* "Every time I change it from the console they need to see that message,
     just below the fuel prices panel" (Gabriel, 2026-08-23). Live off the doc. */
  const [lastEpra, setLastEpra] = useState<{ diesel: number | null; petrol: number | null; at: Date | null } | null>(null);
  const [regionalDefault, setRegionalDefault] = useState<number | null>(null);
  const [caps, setCaps] = useState<{ diesel: number | null; petrol: number | null }>({
    diesel: null,
    petrol: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [pendingName, setPendingName] = useState("");
  const [pendingPlaceId, setPendingPlaceId] = useState("");
  const [pendingPrice, setPendingPrice] = useState("");
  const [pendingFuelType, setPendingFuelType] = useState<StationFuelType>("diesel");
  const [homePin, setHomePin] = useState<{ lat: number; lng: number } | null>(null);
  const [zanaBusy, setZanaBusy] = useState(false);
  const [zanaMsg, setZanaMsg] = useState<string | null>(null);
  const [zanaErr, setZanaErr] = useState<string | null>(null);

  // Station search uses Places API (New) REST — no Maps JS / legacy Autocomplete.
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const canAutocomplete = Boolean(apiKey);

  const docId = `${fleetOrgCode}__${month}`;
  const uniquePlaceCount = useMemo(
    () => new Set(stations.map((s) => s.placeId).filter(Boolean)).size,
    [stations],
  );
  const primaryPlaceCount = useMemo(() => {
    const ids = new Set<string>();
    for (const s of stations) {
      if (s.primary && s.placeId) ids.add(s.placeId);
    }
    return ids.size;
  }, [stations]);
  const primaryAtCap = primaryPlaceCount >= PRIMARY_FUEL_STATION_LIMIT;

  useEffect(() => {
    let cancelled = false;
    /* The price doc is LIVE, the org pin is read once. This used to be one
       getDoc for both, so a ceiling applied from the PEAR console (or by the
       press sweep) sat invisible here until someone pressed "Refresh EPRA
       ceilings" or reloaded the page (Gabriel, 2026-08-22: "it only worked
       after I pressed refresh"). */
    (async () => {
      try {
        const orgSnap = await getDoc(doc(db, "organizations", fleetOrgCode));
        if (cancelled) return;
        const org = orgSnap.data() || {};
        /* The pin is sealed at rest (utils/locationCrypto) on newer schools: a
           plain lat/lng check read it as "no pin", and nearby stations were
           never seeded (2026-10-28). openLatLng reads both shapes. */
        const home = await openLatLng(org.homeLocations);
        if (cancelled) return;
        setHomePin(home);
      } catch (e) {
        console.warn("Failed to load the org pin for fuel prices:", e);
      }
    })();
    const unsub = onSnapshot(
      doc(db, "fuelPrices", docId),
      (priceSnap) => {
        if (cancelled) return;
        const d = priceSnap.data() as FuelPricesDoc | undefined;
        if (d) {
          setStations(
            (Array.isArray(d.stations) ? d.stations : []).map((s) => ({
              ...s,
              fuelType: s.fuelType === "petrol" ? "petrol" : "diesel",
              primary: Boolean(s.primary),
            }))
          );
          setSource(d.source ?? "admin");
          setEpraTown(d.epraTown ?? null);
          setCarriedFrom(d.carriedFrom);
          setLastEpra(
            d.lastEpraCaps && (d.lastEpraCaps.diesel != null || d.lastEpraCaps.petrol != null)
              ? {
                  diesel: d.lastEpraCaps.diesel ?? null,
                  petrol: d.lastEpraCaps.petrol ?? null,
                  at: d.lastEpraRefreshAt && typeof (d.lastEpraRefreshAt as Timestamp).toDate === "function"
                    ? (d.lastEpraRefreshAt as Timestamp).toDate()
                    : null,
                }
              : null,
          );
          setRegionalDefault(d.regionalDefaultPerL ?? null);
          const byType = d.regionalDefaultPerLByType || d.caps || {};
          setCaps({
            diesel: byType.diesel ?? d.regionalDefaultPerL ?? null,
            petrol: byType.petrol ?? null,
          });
        }
        setLoading(false);
      },
      (e) => {
        console.warn("Failed to load fuel prices:", e);
        if (!cancelled) setLoading(false);
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [docId, fleetOrgCode]);

  const persist = useCallback(
    async (next: FuelPriceStation[], nextSource: FuelPricesDoc["source"] = "admin") => {
      setSaving(true);
      try {
        await setDoc(
          doc(db, "fuelPrices", docId),
          {
            orgCode: fleetOrgCode,
            month,
            source: nextSource,
            stations: next,
            regionalDefaultPerL: regionalDefault,
            regionalDefaultPerLByType: {
              diesel: caps.diesel ?? regionalDefault,
              petrol: caps.petrol,
            },
            updatedBy: auth.currentUser?.uid ?? null,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setSource(nextSource);
        setCarriedFrom(undefined);
        setSavedAt(new Date().toLocaleTimeString());
      } catch (e) {
        console.warn("Failed to save fuel prices:", e);
      } finally {
        setSaving(false);
      }
    },
    [docId, fleetOrgCode, month, regionalDefault, caps]
  );

  const addStation = useCallback(() => {
    const price = parseFloat(pendingPrice);
    if (!pendingName.trim() || !Number.isFinite(price) || price <= 0) return;
    const placeId = pendingPlaceId || pendingName.trim();
    const alreadyOnShelf = stations.some((s) => s.placeId === placeId);
    const alreadyPrimary = stations.some((s) => s.placeId === placeId && s.primary);
    // Premium/Enterprise: Add = primary favourite (cap 5 unique places).
    // Basic: no primary cap — they need ~10 typed stations for the shelf.
    const markPrimary = Boolean(isZanaTier);
    if (markPrimary && !alreadyOnShelf && !alreadyPrimary && primaryAtCap) {
      setZanaErr(
        `You already have ${PRIMARY_FUEL_STATION_LIMIT} primary stations. Remove one, or let Zana fill the rest from nearby.`
      );
      return;
    }
    setZanaErr(null);
    // Same place can have diesel AND petrol — add/update the selected type.
    const key = `${pendingFuelType}:${placeId}`;
    const next: FuelPriceStation[] = [
      ...stations.filter((s) => `${s.fuelType || "diesel"}:${s.placeId}` !== key),
      {
        placeId,
        name: pendingName.trim(),
        pricePerL: price,
        fuelType: pendingFuelType,
        primary: markPrimary || alreadyPrimary,
      },
    ];
    // If marking primary, tag sibling fuel-type rows for the same place too.
    const tagged = markPrimary
      ? next.map((s) => (s.placeId === placeId ? { ...s, primary: true } : s))
      : next;
    setStations(tagged);
    setPendingName("");
    setPendingPlaceId("");
    setPendingPrice("");
    void persist(tagged, "admin");
  }, [
    pendingName,
    pendingPlaceId,
    pendingPrice,
    pendingFuelType,
    stations,
    persist,
    isZanaTier,
    primaryAtCap,
  ]);

  const removePlace = useCallback(
    (placeId: string) => {
      const next = stations.filter((s) => s.placeId !== placeId);
      setStations(next);
      void persist(next, "admin");
    },
    [stations, persist]
  );

  const setPlacePrimary = useCallback(
    (placeId: string, makePrimary: boolean) => {
      if (makePrimary) {
        const already = stations.some((s) => s.placeId === placeId && s.primary);
        if (!already && primaryAtCap) {
          setZanaErr(
            `Primary list is full (${PRIMARY_FUEL_STATION_LIMIT}). Unmark another station first.`
          );
          return;
        }
      }
      setZanaErr(null);
      const next = stations.map((s) =>
        s.placeId === placeId ? { ...s, primary: makePrimary } : s
      );
      setStations(next);
      void persist(next, "admin");
    },
    [stations, persist, primaryAtCap]
  );

  /**
   * Premium/Enterprise: Places Nearby around the school pin → fill up to ~10
   * unique stations (after primaries). Keeps primary + hand-tuned prices.
   * Skips placeIds already on the shelf so primary and nearby never duplicate.
   */
  const seedNearbyStations = useCallback(async () => {
    setZanaBusy(true);
    setZanaMsg(null);
    setZanaErr(null);
    try {
      if (!homePin) {
        setZanaErr(
          "Set the school / depot pin first (signup or PEAR console → Home location). Zana needs that to search nearby."
        );
        return;
      }
      const dieselCap = caps.diesel ?? regionalDefault;
      const petrolCap = caps.petrol;
      if (dieselCap == null && petrolCap == null) {
        setZanaErr(
          "No EPRA / regional ceiling on file yet. Tap “Refresh EPRA ceilings” on Analytics → Fuel first, then try again."
        );
        return;
      }
      const found = await fetchNearbyFuelStations(apiKey, homePin.lat, homePin.lng);
      if (!found.ok) {
        setZanaErr(found.message);
        return;
      }

      const byKey = new Map<string, FuelPriceStation>(
        stations.map((s) => [`${s.fuelType || "diesel"}:${s.placeId}`, s])
      );
      const placeIds = new Set(stations.map((s) => s.placeId).filter(Boolean));
      let addedPlaces = 0;
      for (const place of found.places) {
        const already = placeIds.has(place.placeId);
        if (!already && placeIds.size >= NEARBY_FUEL_STATION_TARGET) break;
        for (const ft of ["diesel", "petrol"] as StationFuelType[]) {
          const price = ft === "diesel" ? dieselCap : petrolCap;
          if (price == null || !Number.isFinite(price) || price <= 0) continue;
          const key = `${ft}:${place.placeId}`;
          const existing = byKey.get(key);
          if (existing) {
            // Keep hand-tuned / primary price; refresh name only.
            byKey.set(key, { ...existing, name: place.name });
          } else {
            byKey.set(key, {
              placeId: place.placeId,
              name: place.name,
              pricePerL: Number(price),
              fuelType: ft,
              primary: false,
            });
          }
        }
        if (!already) {
          placeIds.add(place.placeId);
          addedPlaces += 1;
        }
      }
      const next = Array.from(byKey.values());
      setStations(next);
      await persist(
        next,
        uniquePlaceCount === 0 && addedPlaces > 0 ? "zana" : source === "admin" ? "admin" : "zana"
      );
      setZanaMsg(
        addedPlaces > 0
          ? `Zana added ${addedPlaces} nearby station${addedPlaces === 1 ? "" : "s"} (no overlap with your primaries). Edit any Ksh/L — your numbers win.`
          : `Nearby list refreshed — ${found.places.length} result${found.places.length === 1 ? "" : "s"} already on the shelf or overlapped your primaries.`
      );
    } finally {
      setZanaBusy(false);
    }
  }, [
    homePin,
    caps,
    regionalDefault,
    apiKey,
    stations,
    persist,
    uniquePlaceCount,
    source,
  ]);

  const updateStationPrice = useCallback(
    (placeId: string, fuelType: StationFuelType, raw: string) => {
      const price = parseFloat(raw);
      if (!Number.isFinite(price) || price <= 0) return;
      const key = `${fuelType}:${placeId}`;
      const hasRow = stations.some(
        (s) => `${s.fuelType || "diesel"}:${s.placeId}` === key
      );
      const sibling = stations.find((s) => s.placeId === placeId);
      let next: FuelPriceStation[];
      if (hasRow) {
        next = stations.map((s) =>
          `${s.fuelType || "diesel"}:${s.placeId}` === key ? { ...s, pricePerL: price } : s
        );
      } else {
        if (!sibling) return;
        next = [
          ...stations,
          {
            placeId,
            name: sibling.name,
            pricePerL: price,
            fuelType,
            primary: Boolean(sibling.primary),
          },
        ];
      }
      setStations(next);
      void persist(next, "admin");
    },
    [stations, persist]
  );

  /** One card per place — diesel + petrol prices side by side (editable). */
  const stationRows = useMemo(() => {
    const map = new Map<
      string,
      { placeId: string; name: string; diesel?: number; petrol?: number; primary: boolean }
    >();
    for (const s of stations) {
      const id = s.placeId;
      const cur = map.get(id) || {
        placeId: id,
        name: s.name,
        primary: Boolean(s.primary),
      };
      cur.name = s.name || cur.name;
      if (s.primary) cur.primary = true;
      if ((s.fuelType || "diesel") === "petrol") cur.petrol = s.pricePerL;
      else cur.diesel = s.pricePerL;
      map.set(id, cur);
    }
    // Primaries first so the school sees favourites at the top.
    return Array.from(map.values()).sort((a, b) => Number(b.primary) - Number(a.primary));
  }, [stations]);

  /* "(ceiling)" is a claim about provenance, so only EPRA's own figures earn
   * it. A built-in regional guess shown as a ceiling reads as an official
   * maximum the fleet can rely on. */
  const fallbackLabel = source === "epra" ? "(EPRA ceiling)" : "(estimate)";

  const stale = source === "carried_forward" || source === "regional_default";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {!embedded && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "#0369a1" }}
            >
              ← Back
            </button>
          )}
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", margin: 0 }}>
            Fuel prices · {monthLabel(month)}
          </h2>
        </div>
      )}

      {stale && (
        <div
          style={{
            ...CARD,
            background: "#fffbeb",
            border: "1px solid #fde68a",
            padding: 16,
            fontSize: 13.5,
            color: "#92400e",
          }}
        >
          {source === "carried_forward"
            ? `These are still your ${carriedFrom ? monthLabel(carriedFrom) : "previous month"} prices. Every fuel cost we show is calculated from them, so treat those figures as estimates until you update these.`
            : "We have no prices for this fleet, so fuel costs are using a regional average. Add the stations your buses actually use and the numbers become real."}
        </div>
      )}

      {/* Only claim EPRA when the figures actually came from EPRA.
       *
       * This banner used to render on `epraManaged` alone, so an org whose
       * prices were still the built-in regional guess was told "we keep EPRA
       * ceilings on file for you" over the top of the amber banner directly
       * above saying the opposite. `source` already distinguishes the two —
       * nothing needed fetching, the copy just never asked. Matches the rule
       * the backend's own shelf math states: never present a guess as a price. */}
      {epraManaged && !embedded && (
        <div style={{ ...CARD, background: "#f5f3ff", border: "1px solid #ddd6fe", padding: 16, fontSize: 13.5, color: "#5b21b6" }}>
          {source === "epra" ? (
            <>
              We keep EPRA monthly ceilings on file for you — diesel (AGO)
              {caps.diesel != null ? ` ≈ KES ${Number(caps.diesel).toFixed(0)}/L` : ""}
              {caps.petrol != null ? ` · petrol (PMS) ≈ KES ${Number(caps.petrol).toFixed(0)}/L` : ""}
              {epraTown ? `, as published for ${epraTown}` : ""}
              . Those are maxima, not pump prices. A station on this list uses
              the Ksh/L you set for it; any other station uses the EPRA ceiling.
              The driver confirms the shillings — litres are shillings ÷ that price.
              Each bus says whether it burns diesel or petrol at signup.
            </>
          ) : (
            <>
              EPRA's published ceilings are fetched for you every month, but this
              fleet has none on file yet
              {caps.diesel != null || caps.petrol != null ? (
                <>
                  {" — the figures below ("}
                  {caps.diesel != null ? `diesel ≈ KES ${Number(caps.diesel).toFixed(0)}/L` : ""}
                  {caps.diesel != null && caps.petrol != null ? " · " : ""}
                  {caps.petrol != null ? `petrol ≈ KES ${Number(caps.petrol).toFixed(0)}/L` : ""}
                  {") are a rough regional estimate, not EPRA's numbers"}
                </>
              ) : null}
              . Add the stations your buses actually use and the figures become real.
              Each bus says whether it burns diesel or petrol at signup.
            </>
          )}
        </div>
      )}
      {lastEpra && !embedded && (
        <div style={{ ...CARD, background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "12px 16px", fontSize: 13, color: "#166534" }}>
          <b>EPRA ceilings on file</b>
          {lastEpra.diesel != null ? ` — diesel KES ${Number(lastEpra.diesel).toFixed(2)}/L` : ""}
          {lastEpra.petrol != null ? ` · petrol KES ${Number(lastEpra.petrol).toFixed(2)}/L` : ""}
          {lastEpra.at ? ` · updated ${lastEpra.at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}` : ""}
          . These are maxima only — a station on your list keeps the price you typed. This line updates by itself whenever PEAR applies a new ceiling.
        </div>
      )}

      {isZanaTier && (
        <div style={{ ...CARD, background: "#faf5ff", border: "1px solid #e9d5ff", padding: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Ask Zana
          </div>
          <p style={{ fontSize: 13.5, color: "#5b21b6", marginTop: 0, marginBottom: 12 }}>
            Pin up to {PRIMARY_FUEL_STATION_LIMIT} primary stations below (the ones your buses actually use),
            then ask Zana to fill nearby places up to ~{NEARBY_FUEL_STATION_TARGET} total — no duplicates.
            New nearby rows start at EPRA ceilings; edit any pump price.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => void seedNearbyStations()}
              disabled={zanaBusy || saving || !apiKey}
              style={{
                padding: "10px 16px",
                borderRadius: 8,
                border: "none",
                background: zanaBusy ? "#c4b5fd" : "#7c3aed",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13.5,
                cursor: zanaBusy ? "wait" : "pointer",
              }}
            >
              {zanaBusy
                ? "Zana is searching…"
                : uniquePlaceCount === 0
                  ? "Ask Zana to find stations"
                  : "Refresh nearby stations"}
            </button>
          </div>
          {!homePin && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "#b45309" }}>
              No school pin on file yet — set Home location in signup / PEAR console first.
            </div>
          )}
          {zanaMsg && (
            <div style={{ marginTop: 10, fontSize: 13, color: "#16a34a", fontWeight: 600 }}>{zanaMsg}</div>
          )}
          {zanaErr && (
            <div style={{ marginTop: 10, fontSize: 13, color: "#dc2626", fontWeight: 600 }}>{zanaErr}</div>
          )}
        </div>
      )}

      <div style={CARD}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
          {isZanaTier ? "Add a primary station" : "Add a station"}
        </div>
        <p style={{ fontSize: 13, color: "#475569", marginTop: 0, marginBottom: 10 }}>
          {isZanaTier
            ? `Up to ${PRIMARY_FUEL_STATION_LIMIT} primaries — your real pump Ksh/L. Zana’s nearby list fills the rest without overlapping these.`
            : `Add the stations your buses actually use. When a driver stops at one of these, we use that pump’s Ksh/L. Anywhere else uses the EPRA ceiling — they just confirm the amount.`}
        </p>
        {isZanaTier && (
          <div
            style={{
              marginBottom: 14,
              padding: "8px 12px",
              borderRadius: 8,
              background: primaryAtCap ? "#fef3c7" : "#f5f3ff",
              border: `1px solid ${primaryAtCap ? "#fcd34d" : "#ddd6fe"}`,
              fontSize: 13,
              color: primaryAtCap ? "#92400e" : "#5b21b6",
              fontWeight: 600,
            }}
          >
            {primaryPlaceCount} of {PRIMARY_FUEL_STATION_LIMIT} primary stations
            {primaryAtCap ? " — remove or unmark one to add another." : "."}
            {uniquePlaceCount > primaryPlaceCount
              ? ` · ${uniquePlaceCount - primaryPlaceCount} from nearby / other.`
              : ""}
          </div>
        )}
        {!isZanaTier && (
          <div
            style={{
              marginBottom: 14,
              padding: "8px 12px",
              borderRadius: 8,
              background: uniquePlaceCount >= NEARBY_FUEL_STATION_TARGET ? "#f0fdf4" : "#fff7ed",
              border: `1px solid ${uniquePlaceCount >= NEARBY_FUEL_STATION_TARGET ? "#bbf7d0" : "#fed7aa"}`,
              fontSize: 13,
              color: uniquePlaceCount >= NEARBY_FUEL_STATION_TARGET ? "#166534" : "#9a3412",
              fontWeight: 600,
            }}
          >
            {uniquePlaceCount >= NEARBY_FUEL_STATION_TARGET
              ? `${uniquePlaceCount} stations on the shelf — enough for mobile cost estimates.`
              : `${uniquePlaceCount} of ${NEARBY_FUEL_STATION_TARGET} stations — add more below so drivers get real Ksh/L costs.`}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {(["diesel", "petrol"] as StationFuelType[]).map((ft) => (
            <button
              key={ft}
              type="button"
              onClick={() => setPendingFuelType(ft)}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: pendingFuelType === ft ? "1.5px solid #0284c7" : "1px solid #cbd5e1",
                background: pendingFuelType === ft ? "#e0f2fe" : "#fff",
                color: pendingFuelType === ft ? "#0369a1" : "#475569",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {ft}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 260px", minWidth: 220 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
              Station
            </label>
            {canAutocomplete ? (
              <PlacesNewAutocomplete
                apiKey={apiKey}
                value={pendingName}
                onChange={(text) => {
                  setPendingName(text);
                  setPendingPlaceId("");
                }}
                onPick={(p) => {
                  setPendingName(p.name);
                  setPendingPlaceId(p.placeId);
                }}
                placeholder="Shell, Langata Road"
                regionCodes={["ke"]}
                inputStyle={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14 }}
              />
            ) : (
              <input
                value={pendingName}
                onChange={(e) => {
                  setPendingName(e.target.value);
                  setPendingPlaceId("");
                }}
                placeholder="Shell, Langata Road"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14 }}
              />
            )}
            {canAutocomplete && pendingName.trim() && !pendingPlaceId && (
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>
                Pick a suggestion from the list, or Add with the typed name.
              </div>
            )}
          </div>

          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
              Price per litre (KES)
            </label>
            <input
              type="number"
              min={1}
              value={pendingPrice}
              onChange={(e) => setPendingPrice(e.target.value)}
              placeholder="171"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14 }}
            />
          </div>

          <button
            onClick={addStation}
            disabled={
              saving
              || !pendingName.trim()
              || !pendingPrice
              || (Boolean(isZanaTier)
                && primaryAtCap
                && !stations.some((s) => s.placeId === (pendingPlaceId || pendingName.trim())))
            }
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background:
                !pendingName.trim()
                || !pendingPrice
                || (Boolean(isZanaTier)
                  && primaryAtCap
                  && !stations.some((s) => s.placeId === (pendingPlaceId || pendingName.trim())))
                  ? "#cbd5e1"
                  : "#0284c7",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor:
                !pendingName.trim()
                || !pendingPrice
                || (Boolean(isZanaTier)
                  && primaryAtCap
                  && !stations.some((s) => s.placeId === (pendingPlaceId || pendingName.trim())))
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {isZanaTier
              ? primaryPlaceCount === 0
                ? "Add primary"
                : "Add primary"
              : stations.length === 0
                ? "Add"
                : "Add more"}
          </button>
        </div>
      </div>

      <div style={CARD}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            This month&apos;s prices
          </div>
          <div style={{ fontSize: 13, color: "#0f172a", display: "flex", gap: 14, flexWrap: "wrap" }}>
            <span>
              Diesel{" "}
              <strong>
                {caps.diesel != null
                  ? `KES ${Number(caps.diesel).toFixed(0)}/L ${fallbackLabel}`
                  : "—"}
              </strong>
            </span>
            <span>
              Petrol{" "}
              <strong>
                {caps.petrol != null
                  ? `KES ${Number(caps.petrol).toFixed(0)}/L ${fallbackLabel}`
                  : "—"}
              </strong>
            </span>
          </div>
        </div>

        {loading ? (
          <div style={{ fontSize: 13.5, color: "#64748b" }}>Loading…</div>
        ) : stationRows.length === 0 ? (
          <div style={{ fontSize: 13.5, color: "#64748b" }}>
            Nothing yet. Until you add a station, costs use the EPRA ceiling for that bus&apos;s
            fuel type. The driver confirms the shillings at the pump.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 2 }}>
              {source === "zana" || (source === "admin" && stations.every((s) => {
                const cap = (s.fuelType || "diesel") === "petrol" ? caps.petrol : (caps.diesel ?? regionalDefault);
                return cap != null && Math.round(s.pricePerL) === Math.round(Number(cap));
              }))
                ? "Prices started as EPRA ceilings (national maxima) — edit any pump Ksh/L below. Your numbers win."
                : "When the bus is at a station you added, we use that Ksh/L. If it is not on the list, we use the EPRA ceiling. The driver confirms."}
            </div>
            {stationRows.map((row) => (
              <div
                key={row.placeId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: row.primary ? "#f5f3ff" : "#f8fafc",
                  border: row.primary ? "1px solid #ddd6fe" : "1px solid #e2e8f0",
                }}
              >
                <span style={{ flex: "1 1 180px", minWidth: 140 }}>
                  <span style={{ fontSize: 14, color: "#0f172a", display: "block", fontWeight: 600 }}>
                    {row.name}
                    {row.primary && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: "#7c3aed",
                          background: "#ede9fe",
                          padding: "2px 6px",
                          borderRadius: 4,
                          verticalAlign: "middle",
                        }}
                      >
                        Primary
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 11.5, color: "#94a3b8" }}>
                    Diesel + petrol · same station
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569" }}>
                    Diesel
                    <input
                      type="number"
                      min={1}
                      defaultValue={row.diesel != null ? Math.round(row.diesel) : ""}
                      key={`d-${row.placeId}-${row.diesel ?? "x"}`}
                      onBlur={(e) => updateStationPrice(row.placeId, "diesel", e.target.value)}
                      style={{
                        width: 72,
                        padding: "6px 8px",
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    />
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569" }}>
                    Petrol
                    <input
                      type="number"
                      min={1}
                      defaultValue={row.petrol != null ? Math.round(row.petrol) : ""}
                      key={`p-${row.placeId}-${row.petrol ?? "x"}`}
                      onBlur={(e) => updateStationPrice(row.placeId, "petrol", e.target.value)}
                      style={{
                        width: 72,
                        padding: "6px 8px",
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    />
                  </label>
                  {isZanaTier && (
                    <button
                      type="button"
                      onClick={() => setPlacePrimary(row.placeId, !row.primary)}
                      style={{
                        border: "1px solid #c4b5fd",
                        background: row.primary ? "#fff" : "#f5f3ff",
                        borderRadius: 6,
                        padding: "4px 8px",
                        cursor: "pointer",
                        color: "#6d28d9",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {row.primary ? "Unmark primary" : "Make primary"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removePlace(row.placeId)}
                    style={{ border: "none", background: "none", cursor: "pointer", color: "#dc2626", fontSize: 13 }}
                  >
                    Remove
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {savedAt && (
          <div style={{ fontSize: 12, color: "#16a34a", marginTop: 14 }}>Saved at {savedAt}</div>
        )}
      </div>
    </div>
  );
}
